import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import {
  collection,
  deleteDoc,
  doc,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { firestore, updateUser } from '../firebase/firebase';
import { format } from 'date-fns';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ReviewModal from './ReviewModal';
import ClientInfoModal from './ClientInfoModal';
import BookReviewModal from './BookReviewModal';
import ShipModal from './ShipModal';
import PrintModal from './PrintModal';
import { PRODUCTION_STEPS } from '../utils/data';
import Dropdown from 'react-bootstrap/Dropdown';
import SendToPrintModal from './SendToPrintModal';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';
import { SKIP_ANSWER_VALUE, SKIPPED_ANSWER_VALUE } from '../constants/common';
import ErrorToast from './ErrorToast';
import { useUserData } from '../hooks/useUserData';
import { useBookData } from '../hooks/useBookData';
import { IoWarning } from 'react-icons/io5';
import ConfirmModal from './ConfirmModal';
import { calculatePhotosCount } from '../utils/book';

const UsersTable = () => {
  const [reviewModal, setReviewModal] = useState(false);
  const [clientInfoModal, setClientInfoModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [shipModal, setShipModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteGeneratedChaptersModal, setDeleteGeneratedChaptersModal] =
    useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [sendToPrintModal, setSendToPrintModal] = useState(false);
  const [selectedUserUid, setSelectedUserUid] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [startChapter, setChapterStart] = useState(1);
  const [endChapter, setChapterEnd] = useState(50);
  const [searchUser, setSearchUser] = useState(null);
  const [error, setError] = useState(null);
  const [model, setModel] = useState('gpt-5-mini');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    user: null,
    book: null,
    printing: null,
  });
  const [searchEmail, setSearchEmail] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState({});

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const USERS_PER_PAGE = 50;

  const getFilterCategory = useCallback((scope) => {
    if (
      scope === 'all' ||
      scope === 'shouldMigrateToNewDashboard' ||
      scope === 'yetToMigrate' ||
      scope === 'oldBackendUser' ||
      scope === 'hasNoCode'
    ) {
      return 'user';
    }
    if (scope === 'book_status' || scope === 'book_status_all') {
      return 'book';
    }
    if (scope === 'printingStatus' || scope === 'printingStatus_all') {
      return 'printing';
    }
    return null;
  }, []);

  const updateUrlParams = useCallback(
    (filters, email) => {
      const params = new URLSearchParams();

      if (filters.user) {
        params.set('userFilter', filters.user.value);
        params.set('userScope', filters.user.scope);
      }
      if (filters.book) {
        params.set('bookFilter', filters.book.value);
        params.set('bookScope', filters.book.scope);
      }
      if (filters.printing) {
        params.set('printingFilter', filters.printing.value);
        params.set('printingScope', filters.printing.scope);
      }
      if (email) params.set('email', email);

      const newUrl = params.toString()
        ? `${location.pathname}?${params.toString()}`
        : location.pathname;

      navigate(newUrl, { replace: true });
    },
    [location.pathname, navigate]
  );

  const buildBaseQuery = useCallback((filters, searchEmailArg) => {
    if (searchEmailArg) {
      return query(
        collection(firestore, 'users'),
        where('email', '==', searchEmailArg)
      );
    }

    const whereConditions = [];
    const hasPrintingFilter =
      filters.printing !== null &&
      filters.printing.scope !== 'printingStatus_all';

    if (filters.user && filters.user.scope !== 'all') {
      whereConditions.push(where(filters.user.scope, '==', filters.user.value));
    }

    if (filters.book && filters.book.scope !== 'book_status_all') {
      whereConditions.push(where(filters.book.scope, '==', filters.book.value));
    }

    if (filters.printing && filters.printing.scope !== 'printingStatus_all') {
      whereConditions.push(
        where(filters.printing.scope, '==', filters.printing.value)
      );
    }

    const hasBookStatusFilter =
      filters.book !== null && filters.book.scope === 'book_status';

    const orderByClause = hasPrintingFilter
      ? orderBy('print_book', 'desc')
      : hasBookStatusFilter
        ? orderBy(filters.book.value, 'desc')
        : orderBy('created_at', 'desc');

    if (whereConditions.length === 0) {
      return query(collection(firestore, 'users'), orderByClause);
    }

    return query(
      collection(firestore, 'users'),
      ...whereConditions,
      orderByClause
    );
  }, []);

  const fetchInitialUsers = useCallback(
    async (filters, searchEmailArg) => {
      setUsersLoading(true);
      setError(null);

      try {
        const baseQuery = buildBaseQuery(filters, searchEmailArg);
        const paginatedQuery = query(baseQuery, limit(USERS_PER_PAGE));
        const snapshot = await getDocs(paginatedQuery);
        const usersData = [];

        snapshot.forEach((doc) => {
          const data = doc.data();

          const processedUser = {
            ...data,
            uid: doc.id,
            createdAtDate: data?.created_at
              ? format(data?.created_at * 1000, 'yyy MM dd')
              : null,
            lastVisitDate: data?.last_visit_date
              ? format(data?.last_visit_date * 1000, 'yyy MM dd')
              : null,
            generateBookDate: data?.generate_book
              ? format(data?.generate_book * 1000, 'yyy MM dd')
              : null,
            reviewBookDate: data?.review_book
              ? format(data?.review_book * 1000, 'yyy MM dd')
              : null,
            readyForPrintBookDate: data?.ready_for_print_book
              ? format(data?.ready_for_print_book * 1000, 'yyy MM dd')
              : null,
            printBookDate: data?.print_book
              ? format(data?.print_book * 1000, 'yyy MM dd')
              : null,
            shipBookDate: data?.ship_book
              ? format(data?.ship_book * 1000, 'yyy MM dd')
              : null,
          };

          usersData.push(processedUser);
        });

        setUsers(usersData);
        setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMore(snapshot.docs.length === USERS_PER_PAGE);
      } catch (error) {
        setError('Error fetching data. Please try again.');
        console.error('Error:', error);
      } finally {
        setUsersLoading(false);
      }
    },
    [buildBaseQuery]
  );

  const loadMoreUsers = useCallback(
    async (filters, searchEmailArg, lastDocument) => {
      if (!lastDocument || !hasMore || loadingMore) return;

      setLoadingMore(true);
      setError(null);

      try {
        const baseQuery = buildBaseQuery(filters, searchEmailArg);
        const paginatedQuery = query(
          baseQuery,
          startAfter(lastDocument),
          limit(USERS_PER_PAGE)
        );
        const snapshot = await getDocs(paginatedQuery);
        const usersData = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const processedUser = {
            ...data,
            uid: doc.id,
            createdAtDate: data?.created_at
              ? format(data?.created_at * 1000, 'yyy MM dd')
              : null,
            lastVisitDate: data?.last_visit_date
              ? format(data?.last_visit_date * 1000, 'yyy MM dd')
              : null,
            generateBookDate: data?.generate_book
              ? format(data?.generate_book * 1000, 'yyy MM dd')
              : null,
            reviewBookDate: data?.review_book
              ? format(data?.review_book * 1000, 'yyy MM dd')
              : null,
            readyForPrintBookDate: data?.ready_for_print_book
              ? format(data?.ready_for_print_book * 1000, 'yyy MM dd')
              : null,
            printBookDate: data?.print_book
              ? format(data?.print_book * 1000, 'yyy MM dd')
              : null,
            shipBookDate: data?.ship_book
              ? format(data?.ship_book * 1000, 'yyy MM dd')
              : null,
          };

          usersData.push(processedUser);
        });

        setUsers((prev) => [...prev, ...usersData]);
        setLastDoc(snapshot.docs[snapshot.docs.length - 1] || lastDocument);
        setHasMore(snapshot.docs.length === USERS_PER_PAGE);
      } catch (error) {
        setError('Error fetching more data. Please try again.');
        console.error('Error:', error);
      } finally {
        setLoadingMore(false);
      }
    },
    [buildBaseQuery, hasMore, loadingMore]
  );

  const applyFilter = useCallback(
    async (filterValue, filterScope) => {
      setSelectedUserUid(null);
      const category = getFilterCategory(filterScope);

      setActiveFilters((prev) => {
        const currentCategoryFilter = prev[category];
        const isClickingSameFilter =
          currentCategoryFilter?.value === filterValue &&
          currentCategoryFilter?.scope === filterScope;

        const newFilters = { ...prev };

        if (isClickingSameFilter) {
          newFilters[category] = null;
        } else {
          newFilters[category] = { value: filterValue, scope: filterScope };
        }

        setSearchEmail(null);
        updateUrlParams(newFilters, null);
        fetchInitialUsers(newFilters, null);

        return newFilters;
      });
    },
    [getFilterCategory, fetchInitialUsers, updateUrlParams]
  );

  const searchByEmail = useCallback(async () => {
    setSelectedUserUid(null);
    const emptyFilters = { user: null, book: null, printing: null };
    setActiveFilters(emptyFilters);
    setSearchEmail(searchUser);
    updateUrlParams(emptyFilters, searchUser);
    await fetchInitialUsers(emptyFilters, searchUser);
  }, [searchUser, fetchInitialUsers, updateUrlParams]);

  const clearFilters = useCallback(async () => {
    setSelectedUserUid(null);
    const emptyFilters = { user: null, book: null, printing: null };
    setActiveFilters(emptyFilters);
    setSearchEmail(null);
    setSearchUser('');
    updateUrlParams(emptyFilters, null);
    await fetchInitialUsers(emptyFilters, null);
  }, [fetchInitialUsers, updateUrlParams]);

  const refreshUserData = useCallback(async () => {
    await fetchInitialUsers(activeFilters, searchEmail);
  }, [fetchInitialUsers, activeFilters, searchEmail]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emailParam = queryParams.get('email');

    const userFilter = queryParams.get('userFilter');
    const userScope = queryParams.get('userScope');
    const bookFilter = queryParams.get('bookFilter');
    const bookScope = queryParams.get('bookScope');
    const printingFilter = queryParams.get('printingFilter');
    const printingScope = queryParams.get('printingScope');

    const parsedFilters = {
      user:
        userFilter && userScope
          ? {
              value:
                userFilter === 'true'
                  ? true
                  : userFilter === 'false'
                    ? false
                    : userFilter,
              scope: userScope,
            }
          : null,
      book:
        bookFilter && bookScope
          ? { value: bookFilter, scope: bookScope }
          : null,
      printing:
        printingFilter && printingScope
          ? { value: printingFilter, scope: printingScope }
          : null,
    };

    if (emailParam) {
      setActiveFilters({ user: null, book: null, printing: null });
      setSearchEmail(emailParam);
      setSearchUser(emailParam);
      fetchInitialUsers({ user: null, book: null, printing: null }, emailParam);
    } else if (
      parsedFilters.user ||
      parsedFilters.book ||
      parsedFilters.printing
    ) {
      setActiveFilters(parsedFilters);
      setSearchEmail(null);
      fetchInitialUsers(parsedFilters, null);
    } else {
      fetchInitialUsers({ user: null, book: null, printing: null }, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (
          window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight - 200
        ) {
          loadMoreUsers(activeFilters, searchEmail, lastDoc);
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [
    activeFilters,
    searchEmail,
    lastDoc,
    loadMoreUsers,
    loadingMore,
    hasMore,
  ]);

  const filtersData = [
    {
      label: '👤 All',
      value: 'all',
      scope: 'all',
    },
    {
      label: '♻️ Migrated',
      value: true,
      scope: 'shouldMigrateToNewDashboard',
    },
    {
      label: '🏚️ Not migrated',
      value: false,
      scope: 'shouldMigrateToNewDashboard',
    },
    {
      label: '👵🏻 Old backend users',
      value: true,
      scope: 'oldBackendUser',
    },
    {
      label: '❌ No user code',
      value: true,
      scope: 'hasNoCode',
    },
    {
      label: '🛫 Yet to migrate',
      value: true,
      scope: 'yetToMigrate',
    },
    {
      label: '📚 All',
      value: 'all',
      scope: 'book_status_all',
    },
    {
      label: '📕 Generate',
      value: 'generate_book',
      scope: 'book_status',
    },
    {
      label: '📗 Review',
      value: 'review_book',
      scope: 'book_status',
    },
    {
      label: '📘 Ready for print',
      value: 'ready_for_print_book',
      scope: 'book_status',
    },
    {
      label: '📙 Print',
      value: 'print_book',
      scope: 'book_status',
    },
    {
      label: '📒 Ship',
      value: 'ship_book',
      scope: 'book_status',
    },
    {
      label: '🖨️ All',
      value: 'all',
      scope: 'printingStatus_all',
    },
    {
      value: 'CREATED',
      label: '🆕 Created',
      scope: 'printingStatus',
    },
    {
      value: 'UNPAID',
      label: '⚠️ Unpaid',
      scope: 'printingStatus',
    },
    {
      value: 'PAYMENT_IN_PROGRESS',
      label: '💳 Payment in progress',
      scope: 'printingStatus',
    },
    {
      value: 'PRODUCTION_DELAYED',
      label: '🚧 Production delayed',
      scope: 'printingStatus',
    },
    {
      value: 'PRODUCTION_READY',
      label: '🏭 Production ready',
      scope: 'printingStatus',
    },
    {
      label: '📦 In production',
      value: 'IN_PRODUCTION',
      scope: 'printingStatus',
    },
    { label: '🚚 Shipped', value: 'SHIPPED', scope: 'printingStatus' },
    { label: '🏡 Delivered', value: 'DELIVERED', scope: 'printingStatus' },
    {
      value: 'REJECTED',
      label: '⛔️ Rejected',
      scope: 'printingStatus',
    },
    {
      value: 'CANCELED',
      label: '❌ Canceled',
      scope: 'printingStatus',
    },
  ];

  const bookFiltersData = filtersData.filter(
    (filter) =>
      filter.scope === 'book_status' || filter.scope === 'book_status_all'
  );

  const userFiltersData = filtersData.filter(
    (filter) =>
      filter.scope === 'all' ||
      filter.scope === 'shouldMigrateToNewDashboard' ||
      filter.scope === 'oldBackendUser' ||
      filter.scope === 'yetToMigrate' ||
      filter.scope === 'hasNoCode'
  );

  const printingFiltersData = filtersData.filter(
    (filter) =>
      filter.scope === 'printingStatus' || filter.scope === 'printingStatus_all'
  );

  const selectedUser = useMemo(() => {
    if (!selectedUserUid) return {};
    return users.find((u) => u.uid === selectedUserUid) || {};
  }, [users, selectedUserUid]);

  const { emailRepliesLoading, userQuestionsWithAnswers, userData } =
    useUserData(selectedUser);
  const {
    generateChapters,
    constructBookData,
    loading,
    data: bookData,
  } = useBookData(
    selectedUser,
    () => setReviewModal(true),
    startChapter,
    endChapter,
    model
  );

  useEffect(() => {
    async function calculateStatistics() {
      const counts = {};

      for (const filter of filtersData.reverse()) {
        const countKey = `${filter.scope}-${filter.value}`;
        if (filter.scope === 'all') {
          const q = query(collection(firestore, 'users'));
          const snapshot = await getCountFromServer(q);
          counts[countKey] = snapshot.data().count;
        } else {
          const q = query(
            collection(firestore, 'users'),
            where(filter.scope, '==', filter.value)
          );
          const snapshot = await getCountFromServer(q);
          counts[countKey] = snapshot.data().count;
        }
      }

      setCounts(counts);
    }

    calculateStatistics();
  }, []);

  const handleSaveGeneratedAnswers = async () => {
    constructBookData();
    const generatedData = bookData
      .map((i) => ({
        questionId: Number(i?.questionId),
        text: i?.answer || SKIP_ANSWER_VALUE,
        verified: false,
      }))
      ?.reduce((acc, curr) => {
        acc[curr?.questionId] = curr;
        return acc;
      }, {});

    await updateUser({
      uid: selectedUser?.uid,
      data: {
        book_qa_data: {
          ...(userData.book_qa_data || {}),
          ...generatedData,
        },
      },
      email: selectedUser?.email,
    });

    setReviewModal(false);
  };

  const handleSetStatus = async (status) => {
    await updateUser({
      uid: selectedUser?.uid,
      data: {
        [status]: Math.round(Date.now() / 1000),
        book_status: status,
      },
      email: selectedUser?.email,
    });
    setSelectedUserUid(null);
    await refreshUserData();
  };

  const handleRemoveStatusClick = (status) => {
    setSelectedStatus(status);
    setShowConfirmationModal(true);
  };

  const removeSelectedStatus = async () => {
    const statusIndex = PRODUCTION_STEPS.indexOf(selectedStatus);
    const newStatus = PRODUCTION_STEPS[statusIndex - 1];

    const updatedData = {
      [selectedStatus]: null,
      book_status: newStatus,
    };

    if (selectedStatus === 'ready_for_print_book') {
      const oldBookQaData = selectedUser.book_qa_data;
      const newBookQaData = {};

      for (const key in oldBookQaData) {
        if (oldBookQaData.hasOwnProperty(key)) {
          newBookQaData[key] = {
            ...oldBookQaData[key],
            verified: false,
          };
        }
      }

      updatedData.cover = null;
      updatedData.cover_photo = null;
      updatedData.shipping_address = null;
      updatedData.book_qa_data = newBookQaData;
    }

    await updateUser({
      uid: selectedUser?.uid,
      data: updatedData,
      email: selectedUser?.email,
    });
    setShowConfirmationModal(false);
    setSelectedUserUid(null);
    await refreshUserData();
  };

  const deleteUser = async () => {
    const userDoc = doc(firestore, 'users', selectedUserUid);
    await deleteDoc(userDoc);
    setDeleteModal(false);
    setSelectedUserUid(null);
    await refreshUserData();
  };

  const deleteGeneratedChapters = async () => {
    await updateUser({
      uid: selectedUser?.uid,
      data: {
        book_qa_data: {},
      },
    });

    setDeleteGeneratedChaptersModal(false);
    await refreshUserData();
  };

  const handleSetReview = async () => {
    try {
      setError(null);
      const url =
        'https://memowrite.app.n8n.cloud/webhook/73453ac2-fd3b-40e3-82ed-996fc3e96a7e';
      const dataToSend = {
        email: selectedUser?.email,
        language: selectedUser?.language,
      };

      await fetch(url, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
      });
    } catch (error) {
      setError('Error submitting data. Please try again.');
      console.error('Error:', error);
    } finally {
      handleSetStatus('review_book');
    }
  };

  const selectChapterBubbleColor = (text, verified, qId, email) => {
    if (
      startChapter <= qId &&
      endChapter >= qId &&
      selectedUser?.email === email
    ) {
      return 'blue';
    }
    if (verified) {
      return 'green';
    }
    if (text === 'placeholder') {
      return 'grey';
    }
    if ((text || text === SKIP_ANSWER_VALUE) && text !== SKIPPED_ANSWER_VALUE) {
      return 'orange';
    }
    return '#e0e0e0';
  };

  const renderChaptersChart = (user) => {
    if (!user?.email || !user?.uid || !userQuestionsWithAnswers) {
      return null;
    }

    const placeholderData =
      user?.email === selectedUser?.email
        ? userQuestionsWithAnswers.map((i) => ({
            text: i?.answers?.length > 0 ? 'placeholder' : '',
          }))
        : Array(50).fill({ text: '' });
    const chartData = user?.book_qa_data;
    const mergedData = placeholderData?.map((item, idx) => ({
      ...item,
      ...chartData?.[idx + 1],
      questionId: idx + 1,
    }));

    return (
      <div
        style={{
          width: 140,
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 4,
          marginBottom: 8,
        }}
      >
        {mergedData
          ?.sort((a, b) => a?.questionId - b?.questionId)
          ?.map((i) => (
            <div
              key={i?.questionId}
              onClick={() => {
                setChapterStart(i?.questionId);
                setChapterEnd(i?.questionId);
              }}
              style={{
                width: 10,
                height: 10,
                borderRadius: 5,
                cursor: 'pointer',
                backgroundColor: selectChapterBubbleColor(
                  i?.text,
                  i?.verified,
                  i?.questionId,
                  user?.email
                ),
              }}
            ></div>
          ))}
      </div>
    );
  };

  const RemoveButton = ({ status }) => (
    <Button
      size="sm"
      variant="danger"
      onClick={() => handleRemoveStatusClick(status)}
      style={{
        backgroundColor: '#EE632B',
        border: 'none',
        fontSize: 12,
        marginTop: 4,
      }}
    >
      Remove
    </Button>
  );

  const isFilterActive = useCallback(
    (value, scope) => {
      const category = getFilterCategory(scope);
      const categoryFilter = activeFilters[category];
      return categoryFilter?.value === value && categoryFilter?.scope === scope;
    },
    [activeFilters, getFilterCategory]
  );

  const hasAnyActiveFilter =
    activeFilters.user !== null ||
    activeFilters.book !== null ||
    activeFilters.printing !== null;

  const FilterButton = ({ label, value, scope }) => {
    const isActive = isFilterActive(value, scope);
    return (
      <Button
        size="sm"
        variant={isActive ? 'dark' : 'light'}
        onClick={() => applyFilter(value, scope)}
        disabled={usersLoading}
        style={{
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 20,
          padding: '6px 14px',
          border: isActive ? 'none' : '1px solid #dee2e6',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transition: 'all 0.15s ease',
          boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        {label}
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : '#e9ecef',
            color: isActive ? 'white' : '#495057',
            padding: '2px 8px',
            borderRadius: 12,
            minWidth: 24,
            textAlign: 'center',
          }}
        >
          {counts[`${scope}-${value}`] ?? 0}
        </span>
      </Button>
    );
  };
  return (
    <>
      <div style={{ marginTop: 24, marginBottom: 24 }}>
        <h4
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#1e293b',
            marginBottom: 20,
          }}
        >
          Users
        </h4>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{ display: 'flex', flex: 1, minWidth: 280, maxWidth: 400 }}
          >
            <Form.Control
              placeholder="Search by email..."
              value={searchUser || ''}
              onChange={(e) => setSearchUser(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchByEmail()}
              style={{
                borderRadius: '8px 0 0 8px',
                border: '1px solid #e2e8f0',
                borderRight: 'none',
                padding: '10px 14px',
                fontSize: 14,
                boxShadow: 'none',
              }}
            />
            <Button
              variant="dark"
              onClick={searchByEmail}
              disabled={usersLoading}
              style={{
                borderRadius: '0 8px 8px 0',
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaSearch size={14} />
              Search
            </Button>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: '#f8f9fa',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
          border: '1px solid #e9ecef',
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#495057',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            👤 User Status
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {userFiltersData.map(({ label, value, scope }) => (
              <FilterButton
                key={`${scope}-${value}`}
                label={label}
                value={value}
                scope={scope}
              />
            ))}
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid #dee2e6',
            marginBottom: 16,
            paddingTop: 20,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#495057',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            📚 Book Status
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {bookFiltersData.map(({ label, value, scope }) => (
              <FilterButton
                key={`${scope}-${value}`}
                label={label}
                value={value}
                scope={scope}
              />
            ))}
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid #dee2e6',
            paddingTop: 20,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#495057',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            🖨️ Printing Status
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {printingFiltersData.map(({ label, value, scope }) => (
              <FilterButton
                key={`${scope}-${value}`}
                label={label}
                value={value}
                scope={scope}
              />
            ))}
          </div>
        </div>

        {(hasAnyActiveFilter || searchEmail) && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid #dee2e6',
            }}
          >
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={clearFilters}
              disabled={usersLoading}
              style={{
                borderRadius: 20,
                padding: '6px 16px',
                fontSize: 12,
              }}
            >
              ✕ Clear filters
            </Button>
          </div>
        )}
      </div>

      {usersLoading ? (
        <Col
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 400 }}
        >
          <Spinner animation="border" />
        </Col>
      ) : users?.length > 0 ? (
        <>
          <Table
            className="users-table"
            style={{
              fontSize: 14,
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              borderCollapse: 'separate',
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr>
                {[
                  '#',
                  'User',
                  'Generate',
                  'Review',
                  'Ready for print',
                  'Print',
                  'Ship',
                  'Action',
                ].map((header, idx) => (
                  <th
                    key={idx}
                    style={{
                      backgroundColor: '#1a1a2e',
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '14px 12px',
                      borderBottom: 'none',
                      borderRight:
                        idx < 10 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users?.map((user, index) => {
                const arrivalMax =
                  user?.estimatedShippingDates?.arrival_max?.toDate();
                const arrivalMin =
                  user?.estimatedShippingDates?.arrival_min?.toDate();
                const dispatchMax =
                  user?.estimatedShippingDates?.dispatch_max?.toDate();
                const dispatchMin =
                  user?.estimatedShippingDates?.dispatch_min?.toDate();

                const photosCount = calculatePhotosCount(user?.book_qa_photos);

                const dispatchMaxFormatted = dispatchMax
                  ? format(dispatchMax, 'MM dd')
                  : null;
                const dispatchMinFormatted = dispatchMin
                  ? format(dispatchMin, 'MM dd')
                  : null;
                const arrivalMaxFormatted = arrivalMax
                  ? format(arrivalMax, 'MM dd')
                  : null;
                const arrivalMinFormatted = arrivalMin
                  ? format(arrivalMin, 'MM dd')
                  : null;

                const isSelected = selectedUser?.email === user?.email;
                const rowBackground = isSelected
                  ? '#f0fdf4'
                  : index % 2 === 0
                    ? '#ffffff'
                    : '#f8fafc';

                const cellStyle = {
                  padding: '14px 12px',
                  borderBottom: '1px solid #e2e8f0',
                  backgroundColor: rowBackground,
                  verticalAlign: 'top',
                  transition: 'background-color 0.15s ease',
                };

                return (
                  <tr
                    key={user?.uid}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <td style={cellStyle}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#64748b',
                          }}
                        >
                          {index + 1}
                        </span>
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            border: `3px solid ${isSelected ? '#22c55e' : '#cbd5e1'}`,
                            backgroundColor: isSelected
                              ? '#22c55e'
                              : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                          onClick={() => setSelectedUserUid(user.uid)}
                        />
                      </div>
                    </td>
                    <td style={{ ...cellStyle, minWidth: 180 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#1e293b',
                          marginBottom: 6,
                        }}
                      >
                        {user?.email}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: user?.shouldMigrateToNewDashboard
                                ? '#047857'
                                : '#c47e06',
                              backgroundColor: user?.shouldMigrateToNewDashboard
                                ? '#d1fae5'
                                : '#fef3c7',
                              padding: '2px 8px',
                              borderRadius: 4,
                              display: 'inline-block',
                              width: 'fit-content',
                            }}
                          >
                            {user?.shouldMigrateToNewDashboard
                              ? '♻️ Migrated'
                              : '🏚️ Not migrated'}
                          </span>
                          {user?.oldBackendUser && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#6d28d9', //red
                                backgroundColor: '#ede9fe',
                                padding: '2px 8px',
                                borderRadius: 4,
                                display: 'inline-block',
                                width: 'fit-content',
                              }}
                            >
                              👵🏻 Old Backend User
                            </span>
                          )}

                          {!user?.code && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#dc2626',
                                backgroundColor: '#ffe2e2',
                                padding: '2px 8px',
                                borderRadius: 4,
                                display: 'inline-block',
                                width: 'fit-content',
                              }}
                            >
                              ❌ No user code
                            </span>
                          )}
                        </div>
                        {user?.book_status && (
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            🚦 {user.book_status}
                          </span>
                        )}
                        {user?.book_qa_photos && (
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            📷 {photosCount} photos
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          🖥️{' '}
                          {user?.platform
                            ? user?.platform
                            : user?.name
                              ? 'app'
                              : 'email'}
                        </span>
                        {user?.language && (
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            🌐 {user.language}
                          </span>
                        )}
                        {user?.ip && (
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            🛜 {user.ip}
                          </span>
                        )}
                        {user?.order_ids && (
                          <div
                            style={{
                              fontSize: 11,
                              color: '#64748b',
                              marginTop: 4,
                            }}
                          >
                            <div>Order IDs:</div>
                            <div
                              style={{
                                fontWeight: 600,
                                color: '#475569',
                                maxWidth: 180,
                              }}
                            >
                              {user.order_ids}
                            </div>
                          </div>
                        )}
                        {user?.createdAtDate && (
                          <div
                            style={{
                              fontSize: 11,
                              color: '#64748b',
                              marginBottom: 8,
                            }}
                          >
                            <div>Created</div>
                            <div style={{ fontWeight: 600, color: '#334155' }}>
                              {user.createdAtDate}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={cellStyle}>
                      {isSelected && !user?.generateBookDate ? (
                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() => handleSetStatus('generate_book')}
                          style={{ fontSize: 11, borderRadius: 6 }}
                        >
                          Set generate
                        </Button>
                      ) : (
                        <div
                          style={{
                            fontSize: 12,
                            color: user?.generateBookDate
                              ? '#334155'
                              : '#94a3b8',
                            fontWeight: user?.generateBookDate ? 500 : 400,
                          }}
                        >
                          {user?.generateBookDate || '—'}
                        </div>
                      )}
                      {isSelected && user?.generateBookDate && (
                        <RemoveButton status="generate_book" />
                      )}
                    </td>
                    <td style={cellStyle}>
                      {isSelected && !user?.review_book ? (
                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={handleSetReview}
                          style={{ fontSize: 11, borderRadius: 6 }}
                        >
                          Set review
                        </Button>
                      ) : (
                        <div
                          style={{
                            fontSize: 12,
                            color: user?.reviewBookDate ? '#334155' : '#94a3b8',
                            fontWeight: user?.reviewBookDate ? 500 : 400,
                          }}
                        >
                          {user?.reviewBookDate || '—'}
                        </div>
                      )}
                      {isSelected && user?.review_book && (
                        <RemoveButton status="review_book" />
                      )}
                    </td>
                    <td style={cellStyle}>
                      {isSelected && !user?.readyForPrintBookDate ? (
                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() =>
                            handleSetStatus('ready_for_print_book')
                          }
                          style={{ fontSize: 11, borderRadius: 6 }}
                        >
                          Set ready
                        </Button>
                      ) : (
                        <div
                          style={{
                            fontSize: 12,
                            color: user?.readyForPrintBookDate
                              ? '#334155'
                              : '#94a3b8',
                            fontWeight: user?.readyForPrintBookDate ? 500 : 400,
                          }}
                        >
                          {user?.readyForPrintBookDate || '—'}
                        </div>
                      )}
                      {isSelected && user?.readyForPrintBookDate && (
                        <RemoveButton status="ready_for_print_book" />
                      )}
                    </td>
                    <td style={cellStyle}>
                      {isSelected && !user?.printBookDate ? (
                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() => setPrintModal(true)}
                          style={{ fontSize: 11, borderRadius: 6 }}
                        >
                          Set print
                        </Button>
                      ) : (
                        <>
                          <div
                            style={{
                              fontSize: 12,
                              color: user?.printBookDate
                                ? '#334155'
                                : '#94a3b8',
                              fontWeight: user?.printBookDate ? 500 : 400,
                            }}
                          >
                            {user?.printBookDate || '—'}
                          </div>
                          {user?.print_job_order_id && (
                            <div
                              style={{
                                fontSize: 11,
                                color: '#64748b',
                                marginTop: 6,
                              }}
                            >
                              <div>Print job ID:</div>
                              <div
                                style={{ fontWeight: 600, color: '#475569' }}
                              >
                                {user?.print_job_order_id?.id}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {isSelected && user?.printBookDate && (
                        <RemoveButton status="print_book" />
                      )}
                    </td>
                    <td style={cellStyle}>
                      {user?.printingStatus && (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#1e293b',
                            backgroundColor: '#e2e8f0',
                            padding: '4px 8px',
                            borderRadius: 6,
                            display: 'inline-block',
                            marginBottom: 6,
                          }}
                        >
                          📦 {user?.printingStatus}
                        </div>
                      )}
                      {dispatchMinFormatted && dispatchMaxFormatted && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#64748b',
                            marginBottom: 2,
                          }}
                        >
                          🚚 {dispatchMinFormatted} - {dispatchMaxFormatted}
                        </div>
                      )}
                      {arrivalMinFormatted && arrivalMaxFormatted && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#64748b',
                            marginBottom: 6,
                          }}
                        >
                          🏡 {arrivalMinFormatted} - {arrivalMaxFormatted}
                        </div>
                      )}
                      {isSelected && !user?.shipBookDate ? (
                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() => setShipModal(true)}
                          style={{ fontSize: 11, borderRadius: 6 }}
                        >
                          Set ship
                        </Button>
                      ) : (
                        <div
                          style={{
                            fontSize: 12,
                            color: user?.shipBookDate ? '#334155' : '#94a3b8',
                            fontWeight: user?.shipBookDate ? 500 : 400,
                          }}
                        >
                          {user?.shipBookDate || '—'}
                        </div>
                      )}
                      {isSelected && user?.shipBookDate && (
                        <RemoveButton status="ship_book" />
                      )}
                    </td>
                    <td style={cellStyle}>
                      {isSelected ? (
                        emailRepliesLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                            }}
                          >
                            {renderChaptersChart(user)}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <input
                                type="number"
                                value={startChapter}
                                onChange={(e) =>
                                  setChapterStart(e?.target?.value)
                                }
                                style={{
                                  width: 50,
                                  fontSize: 11,
                                  padding: '4px',
                                  borderRadius: 4,
                                  border: '1px solid #cbd5e1',
                                }}
                                min={1}
                              />
                              <input
                                type="number"
                                value={endChapter}
                                onChange={(e) =>
                                  setChapterEnd(e?.target?.value)
                                }
                                style={{
                                  width: 50,
                                  fontSize: 11,
                                  padding: '4px',
                                  borderRadius: 4,
                                  border: '1px solid #cbd5e1',
                                }}
                                max={50}
                              />
                              <Dropdown>
                                <Dropdown.Toggle
                                  id="dropdown-basic"
                                  size="sm"
                                  variant="outline-secondary"
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: 6,
                                  }}
                                />
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    style={{
                                      fontSize: 12,
                                      color:
                                        model === 'gpt-5-mini'
                                          ? 'black'
                                          : 'grey',
                                    }}
                                    onClick={() => setModel('gpt-5-mini')}
                                  >
                                    gpt-5-mini
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    style={{
                                      fontSize: 12,
                                      color:
                                        model === 'gpt-3.5-turbo'
                                          ? 'black'
                                          : 'grey',
                                    }}
                                    onClick={() => setModel('gpt-3.5-turbo')}
                                  >
                                    gpt-3.5-turbo
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    style={{
                                      fontSize: 12,
                                      color:
                                        model === 'gpt-4' ? 'black' : 'grey',
                                    }}
                                    onClick={() => setModel('gpt-4')}
                                  >
                                    gpt-4
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                            <div>
                              <Button
                                size="sm"
                                variant="dark"
                                onClick={generateChapters}
                                disabled={loading}
                                style={{ fontSize: 11, borderRadius: 6 }}
                              >
                                {loading
                                  ? 'Generating...'
                                  : 'Generate chapters'}
                              </Button>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <Button
                                size="sm"
                                variant="dark"
                                onClick={() => {
                                  constructBookData();
                                  setReviewModal(true);
                                }}
                                style={{ fontSize: 11, borderRadius: 6 }}
                              >
                                Review book
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setEditModal(true)}
                                style={{ fontSize: 11, borderRadius: 6 }}
                              >
                                Edit book
                              </Button>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <Button
                                size="sm"
                                variant="outline-dark"
                                onClick={() => setClientInfoModal(true)}
                                style={{ fontSize: 11, borderRadius: 6 }}
                              >
                                View client info
                              </Button>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => setSendToPrintModal(true)}
                                style={{ fontSize: 11, borderRadius: 6 }}
                              >
                                Send to print
                              </Button>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => setDeleteModal(true)}
                                style={{ fontSize: 11, borderRadius: 6 }}
                              >
                                Delete user
                              </Button>
                              {Object.keys(selectedUser?.book_qa_data || {})
                                .length > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() =>
                                    setDeleteGeneratedChaptersModal(true)
                                  }
                                  style={{ fontSize: 11, borderRadius: 6 }}
                                >
                                  Delete chapters
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {loadingMore && (
            <div className="d-flex justify-content-center py-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading more...</span>
            </div>
          )}
          {!hasMore && users.length > 0 && (
            <div className="d-flex justify-content-center py-3">
              <span style={{ fontSize: 14, color: '#666' }}>
                No more users to load
              </span>
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
          }}
        >
          <FaSearch size={20} style={{ marginRight: 4 }} />
          <span>Use search or filters</span>
        </div>
      )}
      {selectedUser?.uid && (
        <>
          <ReviewModal
            visible={reviewModal}
            onClose={() => setReviewModal(false)}
            onSave={handleSaveGeneratedAnswers}
            bookData={bookData}
            user={selectedUser}
          />
          <ClientInfoModal
            visible={clientInfoModal}
            onClose={() => {
              refreshUserData();
              setClientInfoModal(false);
            }}
            clientInfo={selectedUser}
          />
          <ShipModal
            visible={shipModal}
            onClose={() => {
              refreshUserData();
              setShipModal(false);
            }}
            uid={selectedUser?.uid}
            email={selectedUser?.email}
            language={selectedUser?.language}
          />
          <PrintModal
            visible={printModal}
            onClose={() => {
              refreshUserData();
              setPrintModal(false);
            }}
            uid={selectedUser?.uid}
            email={selectedUser?.email}
            language={selectedUser?.language}
          />
          <BookReviewModal
            user={selectedUser}
            visible={editModal}
            onClose={() => {
              refreshUserData();
              setEditModal(false);
            }}
            isAdmin={true}
          />
          <SendToPrintModal
            visible={sendToPrintModal}
            onClose={() => {
              refreshUserData();
              setSendToPrintModal(false);
            }}
            userData={selectedUser}
          />
        </>
      )}
      <ConfirmModal
        visible={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        title={t('dashboardScreen.areYouSure')}
        text={t('dashboardScreen.youWillRemoveStatus')}
        icon={<IoWarning size={26} style={{ marginRight: 4 }} color="orange" />}
        onConfirm={removeSelectedStatus}
      />
      <ConfirmModal
        visible={deleteModal}
        onHide={() => setDeleteModal(false)}
        title={t('dashboardScreen.areYouSure')}
        text={t('dashboardScreen.youWillDeleteUser')}
        icon={<IoWarning size={26} style={{ marginRight: 4 }} color="orange" />}
        onConfirm={deleteUser}
      />
      <ConfirmModal
        visible={deleteGeneratedChaptersModal}
        onHide={() => setDeleteGeneratedChaptersModal(false)}
        title={t('dashboardScreen.areYouSure')}
        text={t('dashboardScreen.youWillDeleteGeneratedChapters')}
        icon={<IoWarning size={26} style={{ marginRight: 4 }} color="orange" />}
        onConfirm={deleteGeneratedChapters}
      />
      {error && <ErrorToast errorMessage={error} />}
    </>
  );
};

export default UsersTable;
